import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminListArticles,
  adminDeleteArticle,
  adminRegenerateCover,
  adminPromoteBackdatedDrafts,
} from "@/lib/articles.functions";
import { formatDate } from "@/lib/site";
import { toast } from "sonner";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

type FilterKey =
  | "all"
  | "with_cover"
  | "missing_cover"
  | "published"
  | "draft"
  | "ready"
  | "scheduled";
type SortKey = "cover" | "title" | "status" | "published_at" | "views";
type SortDir = "asc" | "desc";

const FILTER_KEYS: FilterKey[] = [
  "all",
  "with_cover",
  "missing_cover",
  "published",
  "draft",
  "ready",
  "scheduled",
];
const SORT_KEYS: SortKey[] = ["cover", "title", "status", "published_at", "views"];
const PAGE_SIZES = [25, 50, 100] as const;

export const Route = createFileRoute("/admin/articles")({
  validateSearch: (search: Record<string, unknown>) => {
    const filter = FILTER_KEYS.includes(search.filter as FilterKey)
      ? (search.filter as FilterKey)
      : ("all" as FilterKey);
    const sort = SORT_KEYS.includes(search.sort as SortKey)
      ? (search.sort as SortKey)
      : ("published_at" as SortKey);
    const dir: SortDir = search.dir === "asc" ? "asc" : "desc";
    const pageRaw = Number(search.page);
    const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;
    const pageSizeRaw = Number(search.pageSize);
    const pageSize = (PAGE_SIZES as readonly number[]).includes(pageSizeRaw)
      ? (pageSizeRaw as (typeof PAGE_SIZES)[number])
      : 25;
    return { filter, sort, dir, page, pageSize };
  },
  component: AdminIndex,
});

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "ready", label: "Ready" },
  { key: "scheduled", label: "Scheduled" },
  { key: "published", label: "Published" },
  { key: "with_cover", label: "With cover" },
  { key: "missing_cover", label: "Missing cover" },
];

function AdminIndex() {
  const qc = useQueryClient();
  const navigate = Route.useNavigate();
  const { filter, sort, dir, page, pageSize } = Route.useSearch();
  const [search, setSearch] = useState("");
  const [regenId, setRegenId] = useState<string | null>(null);

  type SearchState = {
    filter: FilterKey;
    sort: SortKey;
    dir: SortDir;
    page: number;
    pageSize: (typeof PAGE_SIZES)[number];
  };
  const setFilter = (f: FilterKey) =>
    navigate({ search: (prev: SearchState) => ({ ...prev, filter: f, page: 1 }), replace: true });
  const setSort = (key: SortKey) =>
    navigate({
      search: (prev: SearchState) => ({
        ...prev,
        sort: key,
        dir: prev.sort === key ? (prev.dir === "asc" ? "desc" : "asc") : "desc",
        page: 1,
      }),
      replace: true,
    });
  const setPage = (p: number) =>
    navigate({ search: (prev: SearchState) => ({ ...prev, page: p }), replace: true });
  const setPageSize = (n: (typeof PAGE_SIZES)[number]) =>
    navigate({ search: (prev: SearchState) => ({ ...prev, pageSize: n, page: 1 }), replace: true });

  // Reset to page 1 when search text changes (skip initial mount so
  // bookmarked/refreshed URLs keep their page)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (page !== 1)
      navigate({ search: (prev: SearchState) => ({ ...prev, page: 1 }), replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);


  const { data, isLoading } = useQuery({
    queryKey: ["admin", "articles"],
    queryFn: () => adminListArticles(),
  });

  const del = useMutation({
    mutationFn: (id: string) => adminDeleteArticle({ data: { id } }),
    onSuccess: () => {
      toast.success("Article deleted");
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to delete"),
  });

  const regen = useMutation({
    mutationFn: (id: string) => adminRegenerateCover({ data: { id } }),
    onMutate: (id) => setRegenId(id),
    onSuccess: () => {
      toast.success("Cover regenerated");
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to regenerate cover"),
    onSettled: () => setRegenId(null),
  });

  const promote = useMutation({
    mutationFn: () => adminPromoteBackdatedDrafts(),
    onSuccess: (res: any) => {
      toast.success(`Promoted ${res?.promoted ?? 0} backdated draft(s) to published`);
      qc.invalidateQueries({ queryKey: ["admin", "articles"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to promote drafts"),
  });

  const backdatedDraftCount = useMemo(
    () => (data ?? []).filter((a: any) => a.status === "draft" && a.published_at).length,
    [data],
  );

  const stats = useMemo(() => {
    const list = data ?? [];
    return {
      total: list.length,
      published: list.filter((a: any) => a.status === "published").length,
      scheduled: list.filter((a: any) => a.status === "scheduled").length,
      ready: list.filter((a: any) => a.status === "ready_for_review").length,
      draft: list.filter((a: any) => a.status === "draft").length,
      missing: list.filter((a: any) => !a.cover_image_url).length,
    };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.filter((a: any) => {
      if (q && !a.title.toLowerCase().includes(q) && !a.slug.toLowerCase().includes(q))
        return false;
      if (filter === "with_cover" && !a.cover_image_url) return false;
      if (filter === "missing_cover" && a.cover_image_url) return false;
      if (filter === "published" && a.status !== "published") return false;
      if (filter === "draft" && a.status !== "draft") return false;
      if (filter === "ready" && a.status !== "ready_for_review") return false;
      if (filter === "scheduled" && a.status !== "scheduled") return false;
      return true;
    });
  }, [data, search, filter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const mult = dir === "asc" ? 1 : -1;
    const getVal = (a: any): { v: any; missing: boolean } => {
      switch (sort) {
        case "cover":
          return { v: a.cover_image_url ? 1 : 0, missing: false };
        case "title":
          return { v: (a.title ?? "").toLowerCase(), missing: !a.title };
        case "status":
          return { v: a.status ?? "", missing: !a.status };
        case "published_at": {
          const t = a.published_at ? new Date(a.published_at).getTime() : NaN;
          return { v: t, missing: !Number.isFinite(t) };
        }
        case "views":
          return { v: a.view_count ?? 0, missing: false };
        default:
          return { v: 0, missing: false };
      }
    };

    arr.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      // Missing values always sink to the bottom
      if (av.missing && !bv.missing) return 1;
      if (!av.missing && bv.missing) return -1;
      if (av.missing && bv.missing) return 0;
      if (av.v < bv.v) return -1 * mult;
      if (av.v > bv.v) return 1 * mult;
      return 0;
    });
    return arr;
  }, [filtered, sort, dir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);
  const endIdx = startIdx + paged.length;

  const statChips: { key: FilterKey; label: string; value: number }[] = [
    { key: "all", label: "Total", value: stats.total },
    { key: "published", label: "Published", value: stats.published },
    { key: "scheduled", label: "Scheduled", value: stats.scheduled },
    { key: "ready", label: "Ready", value: stats.ready },
    { key: "draft", label: "Draft", value: stats.draft },
    { key: "missing_cover", label: "Missing cover", value: stats.missing },
  ];

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="headline-display text-3xl">Articles</h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Every article, any status. Filter by <em>Draft</em> / <em>Ready</em> /{" "}
            <em>Scheduled</em> to see what the{" "}
            <Link to="/admin/backfill" className="underline">
              Backfill
            </Link>{" "}
            and{" "}
            <Link to="/admin" className="underline">
              Dashboard
            </Link>{" "}
            counts refer to. Pre-articles (still just ideas) live on the{" "}
            <Link to="/admin/ideas" search={{ status: "new" }} className="underline">
              Ideas
            </Link>{" "}
            tab.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link
            to="/admin/generate"
            className="border border-border px-3 py-2 text-sm hover:bg-paper-soft"
          >
            Generate
          </Link>
          <Link
            to="/admin/new"
            className="bg-foreground text-background px-3 py-2 text-sm hover:bg-ochre-deep"
          >
            New article
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
        {statChips.map((s) => {
          const active = filter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={
                "border px-3 py-2 text-left transition-colors " +
                (active
                  ? "bg-foreground text-background border-foreground"
                  : "border-border hover:bg-paper-soft")
              }
            >
              <div className="text-xl font-semibold leading-tight">{s.value}</div>
              <div
                className={
                  "text-[11px] uppercase tracking-wider mt-0.5 " +
                  (active ? "text-background/80" : "text-muted-foreground")
                }
              >
                {s.label}
              </div>
            </button>
          );
        })}
      </div>

      {backdatedDraftCount > 0 && (
        <div className="mb-4 border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm flex items-center justify-between gap-3">
          <span>
            <strong>{backdatedDraftCount}</strong> draft
            {backdatedDraftCount === 1 ? "" : "s"} carry a past publish date — invisible on the
            public site and skipped by the publisher.
          </span>
          <button
            onClick={() => promote.mutate()}
            disabled={promote.isPending}
            className="border border-foreground px-3 py-1 text-xs hover:bg-foreground hover:text-background disabled:opacity-50"
          >
            {promote.isPending ? "Promoting…" : "Promote to published"}
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title or slug…"
          className="flex-1 border border-border bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={
                "px-3 py-2 text-xs border transition-colors " +
                (filter === f.key
                  ? "bg-foreground text-background border-foreground"
                  : "border-border hover:bg-paper-soft")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground">
          No articles match.{" "}
          <Link to="/admin/generate" className="underline">
            Generate more
          </Link>
          .
        </div>
      ) : (
        <>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-border">
                <SortHeader
                  label="Cover"
                  active={sort === "cover"}
                  dir={dir}
                  onClick={() => setSort("cover")}
                  className="w-[88px]"
                />
                <SortHeader
                  label="Title"
                  active={sort === "title"}
                  dir={dir}
                  onClick={() => setSort("title")}
                />
                <SortHeader
                  label="Status"
                  active={sort === "status"}
                  dir={dir}
                  onClick={() => setSort("status")}
                />
                <th className="py-2 pr-3 font-medium">AI</th>
                <SortHeader
                  label="Published"
                  active={sort === "published_at"}
                  dir={dir}
                  onClick={() => setSort("published_at")}
                />
                <SortHeader
                  label="Views"
                  active={sort === "views"}
                  dir={dir}
                  onClick={() => setSort("views")}
                />
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((a: any) => {
                const imageType = a.cover_art_direction?.image_type as string | undefined;
                const isRegenning = regenId === a.id && regen.isPending;
                return (
                  <tr
                    key={a.id}
                    className="border-b border-border hover:bg-paper-soft align-top"
                  >
                    <td className="py-2 pr-3">
                      <div className="relative w-16 h-10 bg-paper-soft border border-border overflow-hidden">
                        {a.cover_image_url ? (
                          <img
                            src={a.cover_image_url}
                            alt={a.cover_image_alt ?? a.title}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">
                            No cover
                          </div>
                        )}
                        {isRegenning && (
                          <div className="absolute inset-0 bg-background/70 flex items-center justify-center text-[10px]">
                            …
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <Link
                        to="/admin/edit/$id"
                        params={{ id: a.id }}
                        className="font-medium hover:text-accent"
                      >
                        {a.title}
                      </Link>
                      <div className="text-xs text-muted-foreground">/{a.slug}</div>
                      {imageType && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-border text-muted-foreground">
                          {imageType.replace(/_/g, " ")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={
                          a.status === "published"
                            ? "text-green-700"
                            : a.status === "draft"
                              ? "text-muted-foreground"
                              : "text-red-700"
                        }
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{a.is_ai_generated ? "Yes" : "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {formatDate(a.published_at)}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.view_count ?? 0}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Link
                        to="/article/$slug"
                        params={{ slug: a.slug }}
                        className="text-xs underline mr-3"
                        target="_blank"
                      >
                        view
                      </Link>
                      <Link
                        to="/admin/edit/$id"
                        params={{ id: a.id }}
                        className="text-xs underline mr-3"
                      >
                        edit
                      </Link>
                      <button
                        onClick={() => regen.mutate(a.id)}
                        disabled={isRegenning}
                        className="text-xs underline mr-3 disabled:opacity-50"
                        title={
                          a.cover_image_url
                            ? "Regenerate cover via AI art director"
                            : "Generate cover via AI art director"
                        }
                      >
                        {isRegenning
                          ? "regenerating…"
                          : a.cover_image_url
                            ? "regen cover"
                            : "gen cover"}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete "${a.title}"?`)) del.mutate(a.id);
                        }}
                        className="text-xs text-red-700 underline"
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4">
            <div className="text-xs text-muted-foreground">
              Showing {startIdx + 1}–{endIdx} of {sorted.length}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => setPageSize(Number(v) as (typeof PAGE_SIZES)[number])}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 1 && (
                <PagerControls
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={setPage}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  className,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  className?: string;
}) {
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={"py-2 pr-3 font-medium " + (className ?? "")}>
      <button
        type="button"
        onClick={onClick}
        className={
          "inline-flex items-center gap-1 hover:text-foreground " +
          (active ? "text-foreground" : "text-muted-foreground")
        }
      >
        <span>{label}</span>
        <Icon className="h-3 w-3" />
      </button>
    </th>
  );
}

function PagerControls({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (n: number) => void;
}) {
  const pages = pageRange(page, totalPages);
  return (
    <Pagination className="mx-0 w-auto">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (page > 1) onChange(page - 1);
            }}
            className={page === 1 ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
        {pages.map((p, i) =>
          p === "…" ? (
            <PaginationItem key={`e-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={p}>
              <PaginationLink
                href="#"
                isActive={p === page}
                onClick={(e) => {
                  e.preventDefault();
                  onChange(p as number);
                }}
              >
                {p}
              </PaginationLink>
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (page < totalPages) onChange(page + 1);
            }}
            className={page === totalPages ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function pageRange(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}
